import type {
  FilterConditionCombination,
  FilterConditionContext,
  FilterConditionExpression,
} from './filter';
import type { FilterDetailedText } from './filter-detailed-text';
import type {
  ShareableType,
} from '@bindoc-projects/resource-domain-lib';
import {
  BdChildResourceHelper
} from '@bindoc-projects/resource-domain-lib';
import type { OverrideProperty } from '@bindoc-projects/util-domain-lib';
import { BdEquals } from '@bindoc-projects/util-domain-lib';
import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BD_AND_CONDITION_ID,
  BD_OR_CONDITION_ID,
  BdAndCondition,
  BdOrCondition,
} from '@bindoc-projects/condition-domain-lib';
import { FilterConditionUtil } from './filter.util';
import type { FilterRegistrationToken } from './token';

export class FilterConditionCombinations {
  public static fromObject(
    obj: { type: string; kind: string; expression: Array<object> },
    registeredFilter: FilterRegistrationToken[],
  ): Promise<FilterConditionCombination> {
    switch (obj.kind) {
      case 'AND':
        return FilterConditionAndCombination.fromObject(obj, registeredFilter);
      case 'OR':
        return FilterConditionOrCombination.fromObject(obj, registeredFilter);
      default:
        throw new Error(
          `Missing implementation for FilterConditionCombinations.fromObject for input[${JSON.stringify(obj)}]`,
        );
    }
  }

  public static fromConditionJson(
    conditionId: string,
    jsonObj: unknown,
    registeredFilter: FilterRegistrationToken[],
  ): Promise<FilterConditionCombination> {
    switch (conditionId) {
      case BD_AND_CONDITION_ID:
        return FilterConditionAndCombination.fromConditionJson(
          jsonObj,
          registeredFilter,
        );
      case BD_OR_CONDITION_ID:
        return FilterConditionOrCombination.fromConditionJson(
          jsonObj,
          registeredFilter,
        );
      default:
        throw new Error(
          `Missing implementation for FilterConditionCombinations.fromObject for input[${JSON.stringify(jsonObj)}]`,
        );
    }
  }
}

abstract class FilterConditionAbstractCombination
  implements FilterConditionCombination
{
  static fromObject(
    obj: { type: string; kind: string; expression: Array<object> },
    registeredFilter: FilterRegistrationToken[],
  ): Promise<FilterConditionCombination> {
    throw new Error(JSON.stringify([obj, registeredFilter]));
  }

  static fromConditionJson(
    jsonObj: unknown,
    registeredFilter: FilterRegistrationToken[],
  ): Promise<FilterConditionCombination> {
    throw new Error(JSON.stringify([jsonObj, registeredFilter]));
  }

  protected constructor(public expression: Array<FilterConditionExpression>) {}

  public type = 'combination' as const;

  abstract kind: 'AND' | 'OR';

  abstract clone(): FilterConditionCombination;

  abstract overrideClone(
    override: OverrideProperty[],
  ): FilterConditionCombination;

  abstract toCondition(context?: FilterConditionContext): BdCondition | null;

  abstract kindToText(): string;

  public equals(other: FilterConditionCombination): boolean {
    return (
      BdEquals.equals(this, other, (a, b) => a.type === b.type) &&
      BdEquals.equals(this.kind, other.kind) &&
      BdEquals.equals(
        this.expression,
        other.expression,
        (a, b) => a.length === b.length,
      ) &&
      this.expression.every((it, index) =>
        FilterConditionUtil.equals(it, other.expression.at(index)),
      )
    );
  }

  public valid(): boolean {
    return (
      !!this.kind &&
      (this.expression.length === 0 ||
        this.expression.every((child) => child.valid()))
    );
  }

  public getChildResources(): { [resourceName in ShareableType]?: string[] } {
    return this.expression
      .map((it) => it.getChildResources())
      .reduce(
        (previousValue, currentValue) =>
          BdChildResourceHelper.merge(previousValue, currentValue),
        {},
      );
  }

  public getExtraChildResources(): Record<string, string[]> {
    const merged: Record<string, string[]> = {};
    for (const child of this.expression) {
      const extras = child.getExtraChildResources();
      for (const [key, ids] of Object.entries(extras)) {
        if (!merged[key]) {
          merged[key] = [...ids];
        } else {
          for (const id of ids) {
            if (!merged[key].includes(id)) {
              merged[key].push(id);
            }
          }
        }
      }
    }
    return merged;
  }

  getOverrideProperties(
    tileId: string,
    dependencyVirtualClinicId: string,
  ): OverrideProperty[] {
    return this.expression
      .map((it) => it.getOverrideProperties(tileId, dependencyVirtualClinicId))
      .reduce(
        (previousValue, currentValue) => [...previousValue, ...currentValue],
        [],
      );
  }

  hasRelativeTimeSelection(): boolean {
    return this.expression.some((it) => it.hasRelativeTimeSelection());
  }

  toJSON(): { type: string; kind: string; expression: Array<unknown> } | null {
    return {
      type: this.type,
      kind: this.kind,
      expression: this.expression.filter((it) => !!it).map((it) => it.toJSON()),
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public toDetailedText(_resolvedNames?: Map<string, string>): FilterDetailedText | null {
    return { title: this.toText() ?? '', items: [] };
  }

  toText(): string | null {
    return this.expression
      .filter((it) => !!it)
      .map((it) => {
        if (it.type === 'combination') {
          return `( ${it.toText()} )`;
        } else {
          return it.toText();
        }
      })
      .join(` ${this.kindToText()} `);
  }
}

export class FilterConditionAndCombination extends FilterConditionAbstractCombination {
  kind: 'AND' | 'OR' = 'AND';

  public static override fromObject(
    obj: { type: string; expression: Array<any> },
    registeredFilter: FilterRegistrationToken[],
  ): Promise<FilterConditionCombination> {
    return Promise.all(
      obj.expression.map((it) =>
        FilterConditionUtil.fromObject(it, registeredFilter),
      ),
    ).then((expression) => new FilterConditionAndCombination(expression));
  }

  public static override fromConditionJson(
    jsonObj: any,
    registeredFilter: FilterRegistrationToken[],
  ): Promise<FilterConditionCombination> {
    return Promise.all(
      jsonObj.conditions.map((it) =>
        FilterConditionUtil.fromConditionJson(it, registeredFilter),
      ),
    ).then((expression) => new FilterConditionAndCombination(expression));
  }

  constructor(expression: Array<FilterConditionExpression>) {
    super(expression);
  }

  clone(): FilterConditionCombination {
    return new FilterConditionAndCombination([
      ...this.expression.map((it) => it.clone()),
    ]);
  }

  overrideClone(override: OverrideProperty[]): FilterConditionCombination {
    return new FilterConditionAndCombination(
      this.expression
        .map((it) => it.overrideClone(override))
        .filter((it) => !!it),
    );
  }

  toCondition(context?: FilterConditionContext): BdCondition | null {
    const conditions = this.expression
      .filter((it) => it.valid())
      .map((it) => it.toCondition(context));
    return new BdAndCondition(conditions);
  }

  kindToText(): string {
    return 'und';
  }
}

export class FilterConditionOrCombination extends FilterConditionAbstractCombination {
  kind: 'AND' | 'OR' = 'OR';

  public static override fromObject(
    obj: { type: string; expression: Array<any> },
    registeredFilter: FilterRegistrationToken[],
  ): Promise<FilterConditionCombination> {
    return Promise.all(
      obj.expression.map((it) =>
        FilterConditionUtil.fromObject(it, registeredFilter),
      ),
    ).then((expression) => new FilterConditionOrCombination(expression));
  }

  public static override fromConditionJson(
    jsonObj: any,
    registeredFilter: FilterRegistrationToken[],
  ): Promise<FilterConditionCombination> {
    const children$: Promise<FilterConditionExpression>[] =
      jsonObj.conditions.map((it) =>
        FilterConditionUtil.fromConditionJson(it, registeredFilter),
      );

    return Promise.all(children$).then(
      (expression) => new FilterConditionOrCombination(expression),
    );
  }

  constructor(expression: Array<FilterConditionExpression>) {
    super(expression);
  }

  clone(): FilterConditionCombination {
    return new FilterConditionOrCombination([
      ...this.expression.map((it) => it.clone()),
    ]);
  }

  overrideClone(override: OverrideProperty[]): FilterConditionCombination {
    return new FilterConditionOrCombination(
      this.expression
        .map((it) => it.overrideClone(override))
        .filter((it) => !!it),
    );
  }

  toCondition(context?: FilterConditionContext): BdCondition | null {
    const conditions = this.expression
      .filter((it) => it.valid())
      .map((it) => it.toCondition(context));
    return new BdOrCondition(conditions);
  }

  kindToText(): string {
    return 'oder';
  }
}
